import { useRef, useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Badge,
  IconButton,
  useDisclosure,
  SimpleGrid,
  Dialog,
  Portal,
  Separator,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiEdit2, FiTrash2, FiArrowLeft, FiPhone, FiMail, FiMapPin, FiUser, FiBriefcase, FiPlus } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import Map, { Marker } from 'react-map-gl/maplibre'
import { useWorksiteDetail, useDeleteWorksite } from '../hooks/useBusiness'
import { BusinessFormModal } from '../components/business/BusinessFormModal'
import { AffiliateUnionModal } from '../components/business/AffiliateUnionModal'
import { ArrivalInstructionCard } from '../components/business/ArrivalInstructionCard'
import { InlineEditTextarea } from '../components/shared/InlineEditTextarea'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared'
import { worksiteApi } from '../lib/api'
import { getMapStyle } from '../lib/mapConfig'
import type { WorksiteEmployee, WorksiteUnionAffiliation } from '../types/business'
import 'maplibre-gl/dist/maplibre-gl.css'

function InfoField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="medium">{label}</Text>
      <Text fontSize="sm">{value}</Text>
    </Box>
  )
}

export function WorksiteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const cancelRef = useRef<HTMLButtonElement>(null!)
  const editModal = useDisclosure()
  const affiliateModal = useDisclosure()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const { data: worksite, isLoading, error, refetch } = useWorksiteDetail(id)
  const deleteMutation = useDeleteWorksite()

  const handleSaveNotes = async (_field: string, value: string) => {
    if (!id) return
    try {
      await worksiteApi.updateNotes(id, { notes: value })
      refetch()
      toaster.create({ title: 'Notes updated', type: 'success', duration: 2000 })
    } catch {
      toaster.create({ title: 'Failed to update notes', type: 'error', duration: 3000 })
    }
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteMutation.mutateAsync(id)
      toaster.create({ title: 'Worksite deleted', type: 'success', duration: 3000 })
      navigate('/dashboard/worksites')
    } catch {
      toaster.create({ title: 'Failed to delete worksite', type: 'error', duration: 3000 })
    }
  }

  if (isLoading) {
    return (
      <VStack py={12}>
        <Spinner size="lg" color="accent" />
        <Text color="gray.500">Loading worksite...</Text>
      </VStack>
    )
  }

  if (!!error || !worksite) {
    return (
      <Box p={8}>
        <Box bg="red.50" p={4} borderRadius="md" color="red.600">
          Worksite not found or an error occurred.
        </Box>
        <Button mt={4} variant="ghost" onClick={() => navigate('/dashboard/worksites')}><FiArrowLeft />Back to Worksites
                  </Button>
      </Box>
    );
  }

  const addressLine = [worksite.streetAddress, worksite.city, worksite.state, worksite.zip]
    .filter(Boolean).join(', ')

  const unionColumns: ResponsiveColumn<WorksiteUnionAffiliation>[] = [
    {
      header: 'Union',
      mobileRole: 'title',
      cell: (ua) => <Text fontWeight="medium" fontSize="sm">{ua.unionName}</Text>,
    },
    {
      header: 'Local #',
      mobileRole: 'meta',
      cell: (ua) => <Text fontSize="sm">{ua.localNumber || '---'}</Text>,
    },
    {
      header: 'Union Rep',
      mobileRole: 'meta',
      cell: (ua) => <Text fontSize="sm">{ua.unionRepName || '---'}</Text>,
    },
    {
      header: 'Employer Contact',
      mobileRole: 'meta',
      cell: (ua) => <Text fontSize="sm">{ua.employerContactName || '---'}</Text>,
    },
    {
      header: 'Steward(s)',
      mobileRole: 'meta',
      cell: (ua) => (
        ua.stewards?.length > 0
          ? <>
              {ua.stewards.map(s => (
                <Badge key={s.worksiteStewardId} colorPalette="purple" fontSize="xs" mr={1}>
                  {s.name}{s.role ? ` (${s.role})` : ''}
                </Badge>
              ))}
            </>
          : <Text fontSize="sm" color="gray.400">---</Text>
      ),
    },
    {
      header: 'Contract Start',
      mobileRole: 'meta',
      cell: (ua) => <Text fontSize="sm">{ua.contractStartDate ? new Date(ua.contractStartDate).toLocaleDateString() : '---'}</Text>,
    },
    {
      header: 'Contract End',
      mobileRole: 'meta',
      cell: (ua) => <Text fontSize="sm">{ua.contractEndDate ? new Date(ua.contractEndDate).toLocaleDateString() : '---'}</Text>,
    },
    {
      header: 'Scope',
      mobileRole: 'badge',
      cell: (ua) => (
        <Badge
          colorPalette={ua.businessUnionId ? (ua.isExcluded ? 'red' : 'green') : 'blue'}
          fontSize="xs"
        >
          {ua.businessUnionId ? (ua.isExcluded ? 'Excluded' : 'Covered') : 'Standalone'}
        </Badge>
      ),
    },
  ]

  const employeeColumns: ResponsiveColumn<WorksiteEmployee>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (emp) => <Text fontWeight="medium" fontSize="sm">{emp.contactName}</Text>,
    },
    {
      header: 'Position',
      mobileRole: 'meta',
      cell: (emp) => <Text fontSize="sm">{emp.position || '---'}</Text>,
    },
    {
      header: 'Department',
      mobileRole: 'meta',
      cell: (emp) => <Text fontSize="sm">{emp.department || '---'}</Text>,
    },
    {
      header: 'Primary',
      mobileRole: 'badge',
      cell: (emp) => emp.isPrimary ? <Badge colorPalette="green" fontSize="xs">Primary</Badge> : null,
    },
    {
      header: 'Union',
      mobileRole: 'meta',
      cell: (emp) => emp.unionName ? <Badge colorPalette="purple" fontSize="xs">{emp.unionName}</Badge> : null,
    },
  ]

  return (
    <Box p={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch">
        {/* Back + actions header */}
        <HStack justify="space-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard/worksites')}><FiArrowLeft />Back to Worksites
                      </Button>
          <HStack gap={2}>
            <Button size="sm" colorPalette="brand" onClick={editModal.onOpen}><FiEdit2 />Edit
                          </Button>
            <IconButton
              aria-label="Delete"
              size="sm"
              variant="ghost"
              colorPalette="red"
              onClick={() => setShowDeleteDialog(true)}><FiTrash2 /></IconButton>
          </HStack>
        </HStack>

        {/* Worksite card */}
        <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack gap={4} mb={4}>
            <Box
              w={12} h={12} bg="accent.soft" borderRadius="lg"
              display="flex" alignItems="center" justifyContent="center"
            >
              <Box boxSize={6} color="accent" asChild><FiBriefcase /></Box>
            </Box>
            <Box flex={1}>
              <HStack gap={2}>
                <Text fontSize="xl" fontWeight="bold">{worksite.label}</Text>
                {worksite.type && <Badge bg="info.subtle" color="info.fg">{worksite.type}</Badge>}
                {!worksite.isActive && <Badge colorPalette="red">Inactive</Badge>}
              </HStack>
              {worksite.businessName && (
                <Text fontSize="sm" color="gray.500">Business: {worksite.businessName}</Text>
              )}
              {worksite.description && (
                <Text fontSize="sm" color="gray.600" mt={1}>{worksite.description}</Text>
              )}
            </Box>
          </HStack>

          <Separator mb={4} />

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {addressLine && (
              <HStack align="flex-start" gap={2}>
                <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiMapPin /></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Address</Text>
                  <Text fontSize="sm">{addressLine}</Text>
                </Box>
              </HStack>
            )}
            {worksite.phone && (
              <HStack align="flex-start" gap={2}>
                <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiPhone /></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Phone</Text>
                  <Text fontSize="sm">{worksite.phone}</Text>
                </Box>
              </HStack>
            )}
            {worksite.email && (
              <HStack align="flex-start" gap={2}>
                <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiMail /></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Email</Text>
                  <Text fontSize="sm">{worksite.email}</Text>
                </Box>
              </HStack>
            )}
            {worksite.poc && (
              <HStack align="flex-start" gap={2}>
                <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiUser /></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Point of Contact</Text>
                  <Text fontSize="sm">{worksite.poc}</Text>
                  {worksite.pocPhone && <Text fontSize="xs" color="gray.500">{worksite.pocPhone}</Text>}
                  {worksite.pocEmail && <Text fontSize="xs" color="gray.500">{worksite.pocEmail}</Text>}
                </Box>
              </HStack>
            )}
            <InfoField label="County" value={worksite.county} />
            <InfoField label="Address Type" value={worksite.addressType} />
          </SimpleGrid>

          <Separator my={4} />

          <InlineEditTextarea
            label="Notes"
            value={worksite.notes || ''}
            fieldName="notes"
            canEdit={true}
            onSave={handleSaveNotes}
          />
        </Box>

        {/* Union affiliations (moved above map) */}
        <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack justify="space-between" mb={4}>
            <Text fontSize="lg" fontWeight="semibold">
              Union Affiliations ({worksite.unionAffiliations.length})
            </Text>
            <Button
              size="xs"
              colorPalette="brand"
              variant="outline"
              onClick={affiliateModal.onOpen}><FiPlus />Add
                          </Button>
          </HStack>
          {worksite.unionAffiliations.length === 0 ? (
            <Text color="gray.500" fontSize="sm">No union affiliations.</Text>
          ) : (
            <ResponsiveTable
              bare
              columns={unionColumns}
              rows={worksite.unionAffiliations}
              keyFn={(ua) => ua.worksiteUnionId}
            />
          )}
        </Box>

        {/* O3: arrival instructions (union-tier module, same gate as Union Affiliations) */}
        <ArrivalInstructionCard kind="worksite" id={worksite.id} chainLabel={worksite.businessName} />

        {/* Location mini-map */}
        {worksite.latitude != null && worksite.longitude != null && (
          <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
            <Text fontSize="lg" fontWeight="semibold" mb={3}>Location</Text>
            <Box borderRadius="md" overflow="hidden" h="250px" mx={{ base: -4, md: 0 }}>
              <Map
                initialViewState={{
                  longitude: worksite.longitude,
                  latitude: worksite.latitude,
                  zoom: 15,
                }}
                style={{ width: '100%', height: '100%' }}
                mapStyle={getMapStyle()}
                attributionControl={false}
              >
                <Marker
                  longitude={worksite.longitude}
                  latitude={worksite.latitude}
                  color="#DD6B20"
                />
              </Map>
            </Box>
          </Box>
        )}

        {/* Employees section */}
        <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack justify="space-between" mb={4}>
            <Text fontSize="lg" fontWeight="semibold">
              Employees ({worksite.employees.length})
            </Text>
          </HStack>
          {worksite.employees.length === 0 ? (
            <Text color="gray.500" fontSize="sm">No employees linked to this worksite yet.</Text>
          ) : (
            <ResponsiveTable
              bare
              columns={employeeColumns}
              rows={worksite.employees}
              keyFn={(emp) => emp.employmentId}
              onRowClick={(emp) => navigate(`/dashboard/contacts/${emp.contactId}`)}
            />
          )}
        </Box>
      </VStack>

      {/* Edit modal */}
      {editModal.open && (
        <BusinessFormModal
          isOpen={editModal.open}
          onClose={editModal.onClose}
          worksite={worksite}
        />
      )}

      {/* Affiliate union modal */}
      {affiliateModal.open && (
        <AffiliateUnionModal
          isOpen={affiliateModal.open}
          onClose={affiliateModal.onClose}
          worksiteId={worksite.id}
        />
      )}

      {/* Delete confirmation */}
      <Dialog.Root
        open={showDeleteDialog}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setShowDeleteDialog(false);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">Delete Worksite</Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>{worksite.label}</strong>? This will deactivate the worksite record.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDelete}
                    ml={3}
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </Box>
  );
}
